import type { FC } from "react";
import {
  Book,
  MessageCircle,
  Bug,
  FileText,
  Sparkles,
  Keyboard,
  Code,
  HelpCircle,
  Compass,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDocsDrawer } from "src/contexts/DocsDrawerContext";
import { useWalkthrough } from "src/contexts/WalkthroughContext";
import { DOCS_BASE_URL } from "src/config/docs-mapping";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "src/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "src/components/ui/tooltip";

interface HelpDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const HelpDropdown: FC<HelpDropdownProps> = ({ isOpen, onToggle, onClose }) => {
  const { t, i18n } = useTranslation("common");
  const { openDocs } = useDocsDrawer();
  const { startTour } = useWalkthrough();

  // Get localized docs URL based on current language
  const getLocalizedDocsUrl = (path: string = "") => {
    const baseUrl = DOCS_BASE_URL; // "https://docs.botbat.io"
    const language = i18n.language;

    // Clean base URL (remove trailing slash if exists)
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

    // Check if language is Arabic and add /ar/ prefix
    if (language.startsWith('ar')) {
      // For Arabic, add /ar/ prefix before the path
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      return `${cleanBaseUrl}/ar${cleanPath}`;
    }

    // For other languages, just use the base URL with path
    return path ? `${cleanBaseUrl}${path.startsWith('/') ? path : `/${path}`}` : cleanBaseUrl;
  };

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
        else onToggle();
      }}
    >
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={e => {
                  e.preventDefault();
                  onToggle();
                }}
                className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                data-tour="help-menu"
              >
                <HelpCircle className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{t("navbar.help-and-resources")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" className="min-w-[220px] max-w-[280px]">
        {/* Group 1: Primary Help */}
        <DropdownMenuItem
          onClick={() => openExternal(getLocalizedDocsUrl())}
          className="flex items-center gap-3 cursor-pointer"
        >
          <Book className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.documentation")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to="/help-center"
            className="flex items-center gap-3 cursor-pointer"
            onClick={onClose}
          >
            <MessageCircle className="h-4 w-4 text-gray-400" />
            <span>{t("navbar.contact-support")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openExternal(getLocalizedDocsUrl("/support/report-bug"))}
          className="flex items-center gap-3 cursor-pointer"
        >
          <Bug className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.report-bug")}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Group 2: Updates */}
        <DropdownMenuItem
          onClick={() => openExternal(getLocalizedDocsUrl("/changelog"))}
          className="flex items-center gap-3 cursor-pointer"
        >
          <FileText className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.changelog")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openExternal(getLocalizedDocsUrl("/whats-new"))}
          className="flex items-center gap-3 cursor-pointer"
        >
          <Sparkles className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.whats-new")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            onClose();
            startTour();
          }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <Compass className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.take-a-tour")}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Group 3: Developer */}
        <DropdownMenuItem
          onClick={() => {
            onClose();
            openDocs(i18n.language.startsWith('ar') ? 'ar/' : undefined);
          }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <Keyboard className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.keyboard-shortcuts")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openExternal(getLocalizedDocsUrl("/developer/api-reference"))}
          className="flex items-center gap-3 cursor-pointer"
        >
          <Code className="h-4 w-4 text-gray-400" />
          <span>{t("navbar.api-reference")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HelpDropdown;